import { useCallback } from 'react';

import { Button, Show } from 'shared';
import Box from 'shared_DEPRECATED/components/Box';
import { Nullable } from 'shared_DEPRECATED/types';
import { dateUtils } from 'shared_DEPRECATED/utils';

import { ChallengeCard } from 'features/challenge/components/ChallengeCard';
import { CHALLENGE_LOCATIONS } from 'features/challenge/config';
import {
  INonRepeatedChallenge,
  IRepeatedChallenge,
} from 'features/challenge/config/types';
import {
  CoachingToolCard,
  CoachingToolCardMenu,
  CoachingToolCardMenuCopyToSprint,
} from 'features/coachingTool';
import { CoachingTool } from 'features/coachingTool/CoachingTool';
import {
  PlanningSprintHeader,
  PlanningBoardColumn,
  PlanningBoardColumnItemsCount,
  usePlanningAnalyticsEvents,
  DRAGGABLE_ITEM_TYPES,
  PlanningSprintRestartedHeader,
  SprintOutcome,
} from 'features/planning';
import { useCoachingToolDeleteFromActiveFinishedSprint } from 'features/planning/hooks/useCoachingToolDeleteFromActiveFinishedSprint';
import { SPRINT_STATES } from 'features/sprint/config';
import {
  SprintFinishedMetaData,
  SprintRestartedMetaData,
} from 'features/sprint/config/types';

import {
  BoardColumn,
  BoardColumnHeader,
  BoardColumnWrapper,
} from 'shared/components/Board';

import { useCoachingToolsOnDrop } from '../../hooks/useCoachingToolsOnDrop';
import { CoachingToolsDraggable } from './Backlog/CoachingToolsDraggable';
import { CoachingToolDroppable } from './Backlog/CoachingToolsDroppable';
import { PlanningBoardColumnItemDraggable } from './Column/ItemDraggable';

type PlanningFinishedSprintProps = {
  sprintIndex: number;
  sprintId: string;
  sprintTitle: string;
  sprintOutcomes: SprintOutcome[];
  challenges: (IRepeatedChallenge | INonRepeatedChallenge)[];
  coachingTools: CoachingTool[];
  sprintFormattedDate: string;
  shouldScrollIntoView: boolean;
  sprintWeeksCount: number;
  sprintRestartedMetaData: Nullable<SprintRestartedMetaData>;
  sprintFinished: Nullable<SprintFinishedMetaData>;
  sprintStartDate: string;
  sprintEndDate: string;
};

export const PlanningFinishedSprint = ({
  sprintId,
  sprintTitle,
  sprintOutcomes,
  sprintFormattedDate,
  challenges,
  coachingTools,
  shouldScrollIntoView,
  sprintWeeksCount,
  sprintRestartedMetaData,
  sprintFinished,
  sprintStartDate,
  sprintEndDate,
}: PlanningFinishedSprintProps) => {
  const { sendPlanningViewPastSprintClickedEvent } =
    usePlanningAnalyticsEvents();

  const onViewButtonClick = useCallback(() => {
    sendPlanningViewPastSprintClickedEvent();
  }, [sendPlanningViewPastSprintClickedEvent]);

  const handleCoachingToolRemove =
    useCoachingToolDeleteFromActiveFinishedSprint();

  const handleOnDrop = useCoachingToolsOnDrop({ coachingTools });

  return (
    <PlanningBoardColumn shouldScrollIntoView={shouldScrollIntoView}>
      <Box
        style={{
          height: '100%',
          width: '100%',
          borderRadius: 'var(--border-radius12)',
        }}
      >
        <CoachingToolDroppable
          sprintId={sprintId}
          onDrop={async (item: any) => {
            await handleOnDrop(
              item,
              sprintId,
              CHALLENGE_LOCATIONS.FINISHED_SPRINT
            );
          }}
        >
          <BoardColumnWrapper fraction={1}>
            <BoardColumnHeader minHeight="3rem">
              <PlanningSprintHeader
                title={sprintTitle}
                sprintOutcomes={sprintOutcomes}
                sprintState={SPRINT_STATES.FINISHED}
                sprintFormattedDate={sprintFormattedDate}
                sprintId={sprintId}
                durationInWeeks={sprintWeeksCount}
                sprintFinishedEarlier={sprintFinished?.isFinishedEarlier}
                onViewButtonClick={onViewButtonClick}
                sprintStartDate={sprintStartDate}
                sprintEndDate={sprintEndDate}
              >
                {sprintRestartedMetaData ? (
                  <PlanningSprintRestartedHeader
                    restartedAt={dateUtils(
                      sprintRestartedMetaData!.restartedAt
                    ).format()}
                  />
                ) : null}
              </PlanningSprintHeader>
            </BoardColumnHeader>
            <BoardColumn spacing={1}>
              <Show when={challenges.length > 0}>
                <PlanningBoardColumnItemsCount
                  name="challenge"
                  count={challenges.length}
                />
                {challenges.map((challenge, challengeIndex) => (
                  <PlanningBoardColumnItemDraggable
                    key={`${challenge.challengeId}`}
                    type="challenge"
                    id={challenge.challengeId!}
                    index={challengeIndex}
                    parentId={sprintId}
                    location={CHALLENGE_LOCATIONS.FINISHED_SPRINT}
                  >
                    <ChallengeCard
                      title={challenge.title}
                      picture={challenge.picture}
                      emoji={challenge.emoji}
                      dimensions={challenge.dimensions}
                      target={challenge.target}
                      goals={challenge.goals}
                      tags={challenge.tags}
                      sprintWeeksCount={sprintWeeksCount}
                    />
                  </PlanningBoardColumnItemDraggable>
                ))}
              </Show>
              <Show when={coachingTools.length > 0}>
                <PlanningBoardColumnItemsCount
                  name="HDE Module"
                  count={coachingTools.length}
                />
                {coachingTools.map(({ name, description, picture, id }) => (
                  <CoachingToolsDraggable
                    key={id}
                    item={{
                      id,
                      type: DRAGGABLE_ITEM_TYPES.COACHING_TOOL,
                      fromSprintId: sprintId,
                      fromLocation: CHALLENGE_LOCATIONS.FINISHED_SPRINT,
                    }}
                  >
                    <CoachingToolCard
                      name={name}
                      description={description}
                      url={picture?.url}
                      id={id}
                      key={id}
                    >
                      <CoachingToolCardMenu toolId={id}>
                        <CoachingToolCardMenuCopyToSprint toolId={id} />
                        <Button
                          _focus={{ outline: 'none' }}
                          width="100%"
                          onClick={() => handleCoachingToolRemove(id, sprintId)}
                          justifyContent="flex-start"
                        >
                          Remove
                        </Button>
                      </CoachingToolCardMenu>
                    </CoachingToolCard>
                  </CoachingToolsDraggable>
                ))}
              </Show>
            </BoardColumn>
          </BoardColumnWrapper>
        </CoachingToolDroppable>
      </Box>
    </PlanningBoardColumn>
  );
};
